import { useEffect } from "react"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import { get, includes, isEmpty, map, mapValues, values } from "lodash"
import { DATE } from "helpers/shapes"
import CompareChart from "components/charts/compare"
import CompareCompositionTables from "components/compare-composition-tables"
import CompareSelect from "components/material/compare-select"
import { VIEW_TYPES } from "helpers/base"
import { OK } from "helpers/data-statuses"
import { DATA, DOWNLOAD_FILENAME, TABLE_DOWNLOAD_DATA } from "helpers/shapes/compare-screen"
import { PERCENT_METRICS } from "helpers/comparison"
import useIsConstituentsDownloadAllowed from "hooks/use-is-constituents-download-allowed"
import "./styles.scss"

export default function CompareComposition({
  status,
  data,
  startDate,
  endDate,
  selectedDate,
  setDate,
  metrics,
  selectedMetric,
  setMetric,
  items,
  detailsItems,
  detailsData,
  setGroup,
  saveGroupToStore,
  selectedGroup,
  groups,
  positionsDisplayType,
  setPositionsSorting,
  positionsSorting,
  positionsPagination,
  showPositionsPage,
  setPositionsDisplayType,
  openSecurityProfile,
  openCompositeSecurity,
  missingEquityItemsDates,
  showMissingEquityDates,
  fixedItemNumber,
  hideGraph,
  maxNbColumns,
  viewType,
  saveMetricToStore,
  fileName,
  tableDownloadData,
  modelId,
  positionSets,
}) {
  const [isDownloadAllowedStatus, isDownloadAllowed] = useIsConstituentsDownloadAllowed({ modelId, positionSets })

  useEffect(() => {
    if (!isEmpty(missingEquityItemsDates)) {
      showMissingEquityDates(missingEquityItemsDates)
    }
  }, [missingEquityItemsDates, showMissingEquityDates])

  const selectedView = "composition"
  const selectedMetricId = get(selectedMetric, "id")
  const percents = includes(PERCENT_METRICS, selectedMetricId)
  const chartDownloadData = percents && selectedMetricId !== "modelCoverage"
    ? map(data, (dates) => mapValues(dates, (value, key) => key === "date" ? value : value / 100))
    : data

  return <Paper className="modules_compare-composition" square elevation={1}>
    <div className="wrapper">
      {!hideGraph && <CompareChart
        tooltipFormatter={selectedMetric.formatter}
        margins={selectedMetric.chartMargins}
        metricSelector={<CompareSelect
          options={metrics}
          selectedOption={selectedMetric}
          setSelected={setMetric}
          saveToStore={saveMetricToStore}
          storeName={selectedView}
        />}
        {...{
          status,
          tickFormat: selectedMetric.formatter,
          startDate,
          date: selectedDate,
          setDate,
          endDate,
          data,
          chartDownloadData,
          items,
          fixedItemNumber,
          tableDownloadData,
          fileName,
          selectedGroup,
          selectedMetric,
          positionsDisplayType,
          selectedView,
          percents,
          isDownloadAllowed: isDownloadAllowedStatus === OK && isDownloadAllowed,
        }}
      />}
      <CompareCompositionTables
        items={detailsItems}
        data={detailsData}
        columns={items}
        setPositionsDisplayType={setPositionsDisplayType}
        setGroup={setGroup}
        saveGroupToStore={saveGroupToStore}
        isActive={viewType === VIEW_TYPES.ACTIVE}
        {...{
          groups,
          selectedGroup,
          positionsDisplayType,
          positionsSorting,
          setPositionsSorting,
          positionsPagination,
          showPositionsPage,
          openSecurityProfile,
          openCompositeSecurity,
          fixedItemNumber,
          maxNbColumns,
        }}
      />
    </div>
  </Paper>
}

CompareComposition.propTypes = {
  status: PropTypes.string.isRequired,
  data: DATA.isRequired,
  startDate: DATE.isRequired,
  endDate: DATE.isRequired,
  selectedDate: DATE,
  setDate: PropTypes.func.isRequired,
  metrics: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      chartValue: PropTypes.func.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
  })).isRequired,
  selectedMetric: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    formatter: PropTypes.func.isRequired,
    chartMargins: PropTypes.object,
  }).isRequired,
  setMetric: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    noData: PropTypes.bool,
    isUnmodeled: PropTypes.bool,
  })).isRequired,
  saveMetricToStore: PropTypes.func.isRequired,
  detailsItems: PropTypes.object.isRequired,
  detailsData: PropTypes.array.isRequired,
  setGroup: PropTypes.func.isRequired,
  saveGroupToStore: PropTypes.func.isRequired,
  selectedGroup: PropTypes.object.isRequired,
  groups: PropTypes.array.isRequired,
  positionsDisplayType: PropTypes.object.isRequired,
  setPositionsSorting: PropTypes.func.isRequired,
  positionsSorting: PropTypes.object.isRequired,
  positionsPagination: PropTypes.object.isRequired,
  showPositionsPage: PropTypes.func.isRequired,
  setPositionsDisplayType: PropTypes.func.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func.isRequired,
  missingEquityItemsDates: PropTypes.array,
  showMissingEquityDates: PropTypes.func.isRequired,
  fixedItemNumber: PropTypes.bool,
  hideGraph: PropTypes.bool,
  refEquityDisplayOverride: PropTypes.array,
  maxNbColumns: PropTypes.number,
  currency: PropTypes.string,
  viewType: PropTypes.oneOf(values(VIEW_TYPES)),
  fileName: DOWNLOAD_FILENAME,
  tableDownloadData: TABLE_DOWNLOAD_DATA,
  modelId: PropTypes.string.isRequired,
  positionSets: PropTypes.array.isRequired,
}
